import { ApiError, KrebitApiClient } from "./api";
import {
  clearSession,
  createAuthStartUrl,
  exchangeAuthorizationCode,
  getValidSession,
  hasUsableClientId,
  isValidTenant,
  loadSession,
  loadTenant,
  normalizeTenant,
  saveSession,
  saveTenant,
  verifyTenantExists,
  withUser,
} from "./auth";
import { config, getTenantBaseUrl } from "./config";
import {
  exportCurrentDocumentAsPdf,
  getPendingRestorationNames,
  readExcelExportContext,
  retryExcelRestoration,
  supportsExcelApi,
  watchExcelDocument,
} from "./document";
import { getRecoveryStatus, loadPendingSigning, PendingSigning, recoveryKey, savePendingSigning } from "./recovery";
import { setUiLanguage, t, TranslationKey } from "./i18n";
import {
  AuthSession,
  DialogOAuthMessage,
  ExcelExportContext,
  ExcelExportScope,
  ExcelPageLayoutMode,
  Language,
  PdfExportOptions,
  RecipientInput,
  SigningMethod,
  SigningOrder,
} from "./types";

/* global Office */

interface AppState {
  tenant: string;
  session: AuthSession | null;
  recipients: RecipientInput[];
  activeView: "create" | "settings";
  isBusy: boolean;
  editingRecipientIndex?: number;
  draggedRecipientIndex?: number;
  suppressRecipientClick?: boolean;
  signingSent: boolean;
  pendingSigning?: PendingSigning;
  recoveryMessage?: TranslationKey;
  canRetrySend?: boolean;
  excelContext?: ExcelExportContext;
  excelContextFailed?: boolean;
  documentRevision: number;
  preparedPdf?: { blob: Blob; url: string; filename: string; scope: string };
  defaultLanguage?: Language;
}

const defaultLanguageStorageKey = "krebitSign.defaultLanguage";
const demoUrl = "https://krebitsign.se/boka-demo/";
const contactUrl = "https://krebitsign.se/kontakt/";
const initialTenant = loadTenant();

const state: AppState = {
  tenant: initialTenant,
  session: null,
  recipients: [],
  activeView: initialTenant ? "create" : "settings",
  isBusy: false,
  signingSent: false,
  documentRevision: 0,
};

function createEmptyRecipient(): RecipientInput {
  return {
    first_name: "",
    last_name: "",
    email: "",
    signing_method: "bankid",
  };
}

Office.onReady((info) => {
  if (!isSupportedHost(info.host)) {
    return;
  }

  const sideloadMessage = getElement("sideload-msg");
  const appBody = getElement("app-body");
  sideloadMessage.style.display = "none";
  appBody.style.display = "flex";

  if (state.tenant) {
    state.session = loadSession(state.tenant);
  }

  if (!state.session) {
    state.activeView = "settings";
  }

  initializeFormDefaults();
  applyUiLanguage();
  bindEvents();
  restorePendingSigning();
  render();
  void refreshExcelContext();
  void watchExcelDocument(() => {
    state.documentRevision += 1;
    invalidatePreparedPdf();
    if (!state.isBusy) {
      void refreshExcelContext();
    }
  });
  window.addEventListener("focus", () => {
    if (!state.isBusy) {
      void refreshExcelContext();
    }
  });
});

function bindEvents(): void {
  getElement("tenant-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void saveTenantFromInput();
  });

  getInput("tenant-input").addEventListener("input", () => {
    clearTenantFieldError();
    updateTenantInputSuffix();
  });

  getElement("language-form").addEventListener("submit", (event) => {
    event.preventDefault();
    saveDefaultLanguageFromInput();
  });

  getSelect("settings-language").addEventListener("change", () => {
    updateSaveLanguageButtonState();
  });

  getElement("create-view-button").addEventListener("click", () => {
    syncRecipientsFromDom();
    state.activeView = "create";
    render();
  });

  getElement("settings-view-button").addEventListener("click", () => {
    syncRecipientsFromDom();
    state.activeView = "settings";
    render();
  });

  getElement("sign-in-button").addEventListener("click", () => {
    getElement("auth-window-modal").hidden = false;
  });

  getElement("sign-out-button").addEventListener("click", () => {
    signOut();
  });

  getElement("book-demo-link").addEventListener("click", (event) => {
    event.preventDefault();
    openExternalUrl(demoUrl);
  });

  getElement("contact-krebit-link").addEventListener("click", (event) => {
    event.preventDefault();
    openExternalUrl(contactUrl);
  });

  getElement("new-signing-button").addEventListener("click", () => {
    clearAlert();
    state.signingSent = false;
    resetSigningForm();
    state.recipients = [];
    state.editingRecipientIndex = undefined;
    state.activeView = "create";
    render();
  });

  getElement("add-recipient-button").addEventListener("click", () => {
    state.signingSent = false;
    syncRecipientsFromDom();
    state.recipients.push(createEmptyRecipient());
    openRecipientEditor(state.recipients.length - 1);
  });

  getSelect("signing-order").addEventListener("change", () => {
    syncRecipientsFromDom();
    renderRecipients();
    updateSendButtonState();
    updateResetButtonState();
  });

  getButton("prepare-pdf-button").addEventListener("click", () => {
    void prepareCurrentPdf();
  });
  getButton("preview-pdf-button").addEventListener("click", previewCurrentPdf);
  getInput("pdf-reviewed").addEventListener("change", updateSendButtonState);
  getButton("refresh-sheets-button").addEventListener("click", () => {
    void refreshExcelContext();
  });
  getButton("restore-excel-button").addEventListener("click", () => {
    void restoreExcelSettings();
  });
  getButton("open-recovery-button").addEventListener("click", () => openExternalUrl(getTenantBaseUrl(state.tenant)));
  getButton("check-signing-button").addEventListener("click", () => {
    void recoverSigning(false);
  });
  getButton("retry-send-button").addEventListener("click", () => {
    void recoverSigning(true);
  });
  getButton("clear-recovery-button").addEventListener("click", () => {
    try {
      persistPendingSigning(undefined);
      resetSigningForm();
      state.recipients = [];
      state.editingRecipientIndex = undefined;
      clearAlert();
      render();
    } catch (error) {
      showAlert("error", getErrorMessage(error));
    }
  });

  getButton("excel-export-toggle").addEventListener("click", () => {
    const toggle = getButton("excel-export-toggle");
    const options = getElement("excel-export-options");
    const expanded = toggle.getAttribute("aria-expanded") === "true";

    toggle.setAttribute("aria-expanded", String(!expanded));
    options.hidden = expanded;
  });

  ["excel-export-scope", "excel-page-layout"].forEach((id) => {
    getSelect(id).addEventListener("change", () => {
      invalidatePreparedPdf();
      renderDocument();
      updateResetButtonState();
    });
  });

  ["signing-name", "language", "message"].forEach((id) => {
    getElement(id).addEventListener("input", () => {
      updateSendButtonState();
      updateResetButtonState();
    });
    getElement(id).addEventListener("change", () => {
      updateSendButtonState();
      updateResetButtonState();
    });
  });

  getElement("recipient-back-button").addEventListener("click", () => {
    if (hasUnsavedRecipientEditorChanges()) {
      getElement("discard-recipient-modal").hidden = false;
      return;
    }

    discardRecipientEditorChanges();
  });

  ["recipient-first-name", "recipient-last-name", "recipient-email", "recipient-signing-method"].forEach((id) => {
    getElement(id).addEventListener("input", () => {
      updateRecipientEditorHeading();
      updateAddMeButtonState();
      updateSaveRecipientButtonState();
      updateSendButtonState();
      updateResetButtonState();
    });
    getElement(id).addEventListener("change", () => {
      updateRecipientEditorHeading();
      updateAddMeButtonState();
      updateSaveRecipientButtonState();
      updateSendButtonState();
      updateResetButtonState();
    });
  });

  getElement("add-me-button").addEventListener("click", () => {
    addCurrentUserAsRecipient();
  });

  getElement("save-recipient-button").addEventListener("click", () => {
    if (saveRecipientFromEditor()) {
      closeRecipientEditor();
    }
  });

  getElement("remove-recipient-button").addEventListener("click", () => {
    removeRecipientFromEditor();
  });

  getElement("cancel-discard-recipient-button").addEventListener("click", () => {
    getElement("discard-recipient-modal").hidden = true;
  });

  getElement("confirm-discard-recipient-button").addEventListener("click", () => {
    getElement("discard-recipient-modal").hidden = true;
    discardRecipientEditorChanges();
  });

  getElement("reset-signing-button").addEventListener("click", () => {
    getElement("reset-modal").hidden = false;
  });

  getElement("cancel-reset-button").addEventListener("click", () => {
    getElement("reset-modal").hidden = true;
  });

  getElement("confirm-reset-button").addEventListener("click", () => {
    resetSigningForm();
    state.recipients = [];
    state.editingRecipientIndex = undefined;
    getElement("reset-modal").hidden = true;
    render();
  });

  getElement("ignore-auth-window-button").addEventListener("click", () => {
    getElement("auth-window-modal").hidden = true;
  });

  getElement("allow-auth-window-button").addEventListener("click", () => {
    getElement("auth-window-modal").hidden = true;
    void signIn();
  });

  getElement("signing-form").addEventListener("submit", (event) => {
    event.preventDefault();
    void createAndSendSigning();
  });
}

function initializeFormDefaults(): void {
  const tenantInput = getInput("tenant-input");
  tenantInput.value = state.tenant;
  updateTenantInputSuffix();

  state.defaultLanguage = loadDefaultLanguage() || getOfficeDefaultLanguage();

  const language = getSelect("language");
  language.value = getDefaultLanguage();
  getSelect("settings-language").value = getDefaultLanguage();

  getSelect("signing-order").value = "parallel";
  getSelect("excel-export-scope").value = "active-sheet";
  getSelect("excel-page-layout").value = "preserve";
  updateSaveLanguageButtonState();
}

function getDefaultLanguage(): Language {
  return state.defaultLanguage || getOfficeDefaultLanguage();
}

function getOfficeDefaultLanguage(): Language {
  const displayLanguage = Office.context.displayLanguage || "";
  return displayLanguage.toLowerCase().startsWith("sv") ? "SV" : "EN";
}

function isSupportedHost(host: Office.HostType | null | undefined): boolean {
  return host === Office.HostType.Word || host === Office.HostType.Excel;
}

function applyUiLanguage(): void {
  const language = getDefaultLanguage();
  setUiLanguage(language);
  document.documentElement.lang = language === "SV" ? "sv" : "en";

  document.querySelectorAll<HTMLElement>("[data-i18n]").forEach((element) => {
    const key = element.dataset.i18n as TranslationKey | undefined;

    if (key) {
      element.textContent = t(key);
    }
  });

  getElement("app-nav").setAttribute("aria-label", t("viewsLabel"));
  getButton("recipient-back-button").setAttribute("aria-label", t("backToSigning"));
  getButton("reset-signing-button").setAttribute("aria-label", t("resetSigning"));
  getInput("tenant-input").placeholder = t("workspacePlaceholder");
  updateTenantInputSuffix();
}

function loadDefaultLanguage(): Language | undefined {
  const language = localStorage.getItem(defaultLanguageStorageKey);
  return language === "SV" || language === "EN" ? language : undefined;
}

function saveDefaultLanguage(language: Language): void {
  localStorage.setItem(defaultLanguageStorageKey, language);
}

async function saveTenantFromInput(): Promise<void> {
  const tenant = normalizeTenant(getInput("tenant-input").value);

  if (!isValidTenant(tenant)) {
    showWorkspaceNotFoundAlert();
    getInput("tenant-input").focus();
    return;
  }

  try {
    clearAlert();
    clearTenantFieldError();
    setBusy(true, t("checkingWorkspace"));
    await verifyTenantExists(tenant);

    state.tenant = tenant;
    state.session = loadSession(tenant);
    invalidatePreparedPdf();
    restorePendingSigning();
    state.signingSent = false;
    saveTenant(tenant);
    showAlert("success", t("using"), `${tenant}.krebit.se`);
    render();

    if (!state.session) {
      getElement("auth-window-modal").hidden = false;
    }
  } catch (error) {
    if (isWorkspaceNotFoundError(error)) {
      showWorkspaceNotFoundAlert();
      getInput("tenant-input").focus();
      return;
    }

    showAlert("error", getErrorMessage(error));
  } finally {
    setBusy(false);
  }
}

function saveDefaultLanguageFromInput(): void {
  const language = getSelect("settings-language").value as Language;

  if (language !== "SV" && language !== "EN") {
    showAlert("error", t("chooseLanguage"));
    return;
  }

  const previousLanguage = getDefaultLanguage();
  state.defaultLanguage = language;
  saveDefaultLanguage(language);
  applyUiLanguage();

  if (getSelect("language").value === previousLanguage) {
    getSelect("language").value = language;
    updateSendButtonState();
    updateResetButtonState();
  }

  showAlert("success", t("defaultLanguageSaved"), language === "SV" ? t("swedish") : t("english"));
  render();
}

async function signIn(): Promise<void> {
  try {
    clearAlert();
    ensureTenant();
    setBusy(true, t("checkingWorkspace"));
    await verifyTenantExists(state.tenant);

    if (!hasUsableClientId()) {
      throw new Error(t("authUnavailable"));
    }

    setBusy(true, t("openingAuthentication"));
    const authUrl = await createAuthStartUrl(state.tenant, getDefaultLanguage());
    const message = await openAuthDialog(authUrl);

    if (message.error) {
      throw new Error(message.errorDescription || message.error);
    }

    const session = await exchangeAuthorizationCode(message);
    const authenticatedSession = await loadCurrentUser(session);
    state.session = authenticatedSession;
    state.activeView = "create";
    state.signingSent = false;
    restorePendingSigning();
    showAlert("success", t("signedInToKrebit"));
  } catch (error) {
    if (isWorkspaceNotFoundError(error)) {
      showWorkspaceNotFoundAlert();
    } else {
      showAlert("error", getErrorMessage(error));
    }
  } finally {
    setBusy(false);
    render();
  }
}

function openAuthDialog(url: string): Promise<DialogOAuthMessage> {
  return new Promise((resolve, reject) => {
    Office.context.ui.displayDialogAsync(url, { height: 65, width: 45, promptBeforeOpen: false }, (result) => {
      if (result.status !== Office.AsyncResultStatus.Succeeded) {
        reject(new Error(result.error?.message || t("cannotOpenAuth")));
        return;
      }

      const dialog = result.value;

      dialog.addEventHandler(Office.EventType.DialogMessageReceived, (event) => {
        if (!("message" in event)) {
          return;
        }

        try {
          const message = JSON.parse(event.message) as DialogOAuthMessage;
          dialog.close();
          resolve(message);
        } catch (_error) {
          dialog.close();
          reject(new Error(t("cannotCompleteSignIn")));
        }
      });

      dialog.addEventHandler(Office.EventType.DialogEventReceived, (event: { error: number }) => {
        reject(new Error(`${t("dialogClosed")} (${event.error})`));
      });
    });
  });
}

async function loadCurrentUser(session: AuthSession): Promise<AuthSession> {
  const client = new KrebitApiClient(session);
  const user = await client.getCurrentUser();
  return withUser(session, user);
}

function signOut(): void {
  if (state.tenant) {
    clearSession(state.tenant);
  }

  state.session = null;
  state.pendingSigning = undefined;
  invalidatePreparedPdf();
  state.activeView = "settings";
  state.signingSent = false;
  showAlert("success", t("signedOut"));
  render();
}

async function createAndSendSigning(): Promise<void> {
  if (state.isBusy || state.pendingSigning) {
    return;
  }
  try {
    clearAlert();
    ensureTenant();
    if (!state.session) {
      throw new Error(t("loginBeforeCreate"));
    }
    const form = readSigningForm();
    const recipients = readRecipients();
    const prepared = state.preparedPdf;
    if (!prepared || !getInput("pdf-reviewed").checked) {
      throw new Error(t("pdfReviewRequired"));
    }
    setBusy(true, t("checkingSignIn"));
    const validSession = await getValidSession(state.session);
    state.session = validSession;
    saveSession(validSession);
    // A document-change event can arrive while sign-in is being refreshed.
    if (state.preparedPdf !== prepared) {
      throw new Error(t("pdfChanged"));
    }
    // Also guard against another task pane having started a signing meanwhile.
    const existing = loadPendingSigning(getRecoveryKey());
    if (existing) {
      state.pendingSigning = existing;
      throw new Error(t("recoveryHelp"));
    }
    const api = new KrebitApiClient(validSession);
    setBusy(true, t("creatingSigning"));
    persistPendingSigning({ name: form.name, stage: "creating" });
    const signing = await api.createSigning(form);
    if (!signing.id) {
      throw new Error(t("recoveryUnknown"));
    }
    persistPendingSigning({ id: signing.id, name: form.name, stage: "uploading" });
    setBusy(true, t("addingDocument"));
    await api.uploadDocument(signing.id, prepared.blob, prepared.filename);
    persistPendingSigning({ ...state.pendingSigning, stage: "recipients" });
    for (let index = 0; index < recipients.length; index += 1) {
      setBusy(true, `${t("addingRecipient")} ${index + 1} ${t("of")} ${recipients.length}...`);
      await api.addRecipient(signing.id, recipients[index]);
    }
    persistPendingSigning({ ...state.pendingSigning, stage: "sending" });
    setBusy(true, t("sendingSigning"));
    await api.sendSigning(signing.id);
    finishSigning();
  } catch (error) {
    // These responses explicitly reject creation; a network failure or 5xx may have created it.
    if (
      state.pendingSigning?.stage === "creating" &&
      error instanceof ApiError &&
      [400, 401, 403, 404, 422].indexOf(error.status) >= 0
    ) {
      persistPendingSigning(undefined);
    }
    showAlert("error", getErrorMessage(error));
  } finally {
    setBusy(false);
    render();
  }
}

function getRecoveryKey(): string {
  return recoveryKey(state.tenant, state.session?.user?.email || "");
}

function persistPendingSigning(pending: PendingSigning | undefined): void {
  // Keep an ID in memory even if persisting the response fails.
  if (pending) {
    state.pendingSigning = pending;
  }
  savePendingSigning(getRecoveryKey(), pending);
  state.pendingSigning = pending;
  state.recoveryMessage = undefined;
  state.canRetrySend = false;
}

function restorePendingSigning(): void {
  state.pendingSigning = undefined;
  state.canRetrySend = false;
  state.recoveryMessage = undefined;
  if (state.session) {
    try {
      state.pendingSigning = loadPendingSigning(getRecoveryKey());
    } catch (_error) {
      // Do not silently allow another send if recovery information cannot be read.
      state.pendingSigning = { name: t("signing"), stage: "creating" };
      state.recoveryMessage = "recoveryStatusUnknown";
    }
  }
}

function finishSigning(): void {
  persistPendingSigning(undefined);
  resetSigningForm();
  state.recipients = [];
  state.editingRecipientIndex = undefined;
  state.signingSent = true;
  clearAlert();
  scrollToTop();
}

async function recoverSigning(send: boolean): Promise<void> {
  const pending = state.pendingSigning;
  if (state.isBusy || !pending?.id || !state.session) {
    return;
  }
  state.canRetrySend = false;
  try {
    clearAlert();
    setBusy(true, t("checkingSigningStatus"));
    state.session = await getValidSession(state.session);
    saveSession(state.session);
    const api = new KrebitApiClient(state.session);
    const status = getRecoveryStatus(await api.getSigning(pending.id));
    if (status === "sent") {
      finishSigning();
    } else if (status === "draft") {
      if (send && pending.stage === "sending") {
        // Only retry the final step, after a fresh status check. Never re-upload or recreate.
        setBusy(true, t("sendingSigning"));
        await api.sendSigning(pending.id);
        finishSigning();
      } else {
        state.canRetrySend = pending.stage === "sending";
        state.recoveryMessage = state.canRetrySend ? "recoveryReady" : "recoveryDraft";
      }
    } else {
      state.recoveryMessage = status === "cancelled" ? "recoveryCancelled" : "recoveryStatusUnknown";
    }
  } catch (error) {
    state.recoveryMessage = "recoveryStatusUnknown";
    showAlert("error", getErrorMessage(error));
  } finally {
    setBusy(false);
    render();
  }
}

async function prepareCurrentPdf(): Promise<void> {
  if (state.isBusy) {
    return;
  }
  const revision = state.documentRevision;
  try {
    clearAlert();
    invalidatePreparedPdf();
    setBusy(true, t("preparingDocument"));
    // Resolve the actual sheet before authentication or any other lengthy work.
    state.excelContext = (await readExcelExportContext()) || undefined;
    state.excelContextFailed = false;
    const options = readPdfExportOptions();
    const filename = getCurrentDocumentPdfName();
    const scope = getExcelScopeLabel();
    const blob = await exportCurrentDocumentAsPdf(config.maxPdfBytes, options);
    if (state.documentRevision !== revision) {
      throw new Error(t("pdfChanged"));
    }
    state.preparedPdf = { blob, filename, scope, url: URL.createObjectURL(blob) };
  } catch (error) {
    showAlert("error", getErrorMessage(error));
  } finally {
    setBusy(false);
    render();
  }
}

function previewCurrentPdf(): void {
  if (state.isBusy || !state.preparedPdf) {
    return;
  }
  const previewWindow = window.open("about:blank", "_blank");
  if (!previewWindow) {
    showAlert("error", t("pdfPopupBlocked"));
    return;
  }
  previewWindow.opener = null;
  previewWindow.location.href = state.preparedPdf.url;
}

function invalidatePreparedPdf(): void {
  if (state.preparedPdf) {
    URL.revokeObjectURL(state.preparedPdf.url);
    state.preparedPdf = undefined;
  }
  getInput("pdf-reviewed").checked = false;
  renderDocument();
  updateSendButtonState();
}

async function refreshExcelContext(): Promise<void> {
  if (!isExcelHost() || state.isBusy || getPendingRestorationNames().length) {
    return;
  }
  try {
    const previous = state.excelContext;
    const current = (await readExcelExportContext()) || undefined;
    if (state.isBusy) {
      return;
    }
    state.excelContext = current;
    state.excelContextFailed = false;
    if (
      previous &&
      current &&
      ((getSelect("excel-export-scope").value === "active-sheet" &&
        previous.activeWorksheetId !== current.activeWorksheetId) ||
        JSON.stringify(previous.visibleWorksheets) !== JSON.stringify(current.visibleWorksheets))
    ) {
      invalidatePreparedPdf();
    }
  } catch (_error) {
    state.excelContextFailed = true;
  }
  renderDocument();
}

async function restoreExcelSettings(): Promise<void> {
  if (state.isBusy) {
    return;
  }
  try {
    setBusy(true, t("restoreExcel"));
    await retryExcelRestoration();
    showAlert("success", t("excelRestored"));
  } catch (error) {
    showAlert("error", getErrorMessage(error));
  } finally {
    setBusy(false);
    render();
  }
}

function readSigningForm() {
  const name = getInput("signing-name").value.trim();
  const language = getSelect("language").value as Language;
  const signingOrder = getSelect("signing-order").value as SigningOrder;
  const message = getInput("message").value.trim();

  if (!name) {
    throw new Error(t("enterSigningName"));
  }

  if (language !== "SV" && language !== "EN") {
    throw new Error(t("chooseLanguage"));
  }

  if (signingOrder !== "parallel" && signingOrder !== "sequential") {
    throw new Error(t("chooseOrder"));
  }

  return {
    name,
    language,
    message,
    signing_order: signingOrder,
  };
}

function readRecipients(): RecipientInput[] {
  const recipients = syncRecipientsFromDom();

  if (!recipients.length) {
    throw new Error(t("addRecipientBeforeSending"));
  }

  recipients.forEach((recipient) => {
    if (!isRecipientComplete(recipient)) {
      throw new Error(t("recipientRequired"));
    }
  });

  return recipients;
}

function readPdfExportOptions(): PdfExportOptions {
  if (!isExcelHost()) {
    return {};
  }

  const scope = getSelect("excel-export-scope").value as ExcelExportScope;
  const pageLayout = getSelect("excel-page-layout").value as ExcelPageLayoutMode;

  if (
    (scope !== "active-sheet" && scope !== "workbook") ||
    (pageLayout !== "fit-one-page" && pageLayout !== "fit-columns" && pageLayout !== "preserve")
  ) {
    throw new Error(t("excelExportSettingsFailure"));
  }

  return { excel: { scope, pageLayout, context: state.excelContext } };
}

function render(): void {
  getInput("tenant-input").value = state.tenant;
  updateTenantInputSuffix();

  const tenantConfigured = Boolean(state.tenant);
  const signedIn = Boolean(state.session);
  const activeView = tenantConfigured && signedIn ? state.activeView : "settings";

  getElement("create-view").hidden = activeView !== "create";
  getElement("settings-view").hidden = activeView !== "settings";
  getElement("app-nav").hidden = !tenantConfigured || !signedIn;
  getElement("user-avatar").hidden = !tenantConfigured || !signedIn;
  updateNavState(activeView);
  getElement("first-run-section").hidden = signedIn;
  getElement("settings-domain-card").hidden = !signedIn;
  getElement("language-form").hidden = false;
  getElement("account-acquisition-section").hidden = signedIn;
  getElement("auth-section").hidden = !tenantConfigured || !signedIn;
  getElement("signing-section").hidden =
    !tenantConfigured || !signedIn || state.signingSent || Boolean(state.pendingSigning);
  getElement("sent-section").hidden = !tenantConfigured || !signedIn || !state.signingSent;
  getElement("signing-recovery").hidden = !signedIn || !state.pendingSigning;
  getElement("recovery-name").textContent = state.pendingSigning?.name || "";
  getElement("recovery-message").textContent = t(
    state.recoveryMessage || (state.pendingSigning?.id ? "recoveryNeedsCheck" : "recoveryUnknown")
  );
  getButton("check-signing-button").hidden = !state.pendingSigning?.id;
  getButton("retry-send-button").hidden = !state.canRetrySend;
  renderDocument();
  getElement("tenant-display").textContent = tenantConfigured ? `${state.tenant}.krebit.se` : t("noDomainSelected");

  const user = state.session?.user;
  renderUserAvatar(user);
  getElement("user-avatar").title = signedIn
    ? user?.email
      ? `${t("loggedInAs")} ${user.email}`
      : t("loggedIn")
    : t("notSignedIn");
  getElement("auth-status").textContent = signedIn
    ? user?.email
      ? `${t("loggedInAs")} ${user.email}`
      : t("loggedIn")
    : t("notSignedIn");

  getElement("sign-in-button").hidden = signedIn;
  getElement("sign-out-button").hidden = !signedIn;
  renderTenantSubmitButton(signedIn);

  if (!hasUsableClientId()) {
    showAlert("error", t("authUnavailable"));
  }

  renderRecipients();
  renderRecipientEditor();
  renderSigningSubView();
  setControlsDisabled(state.isBusy);
  updateSendButtonState();
  updateResetButtonState();
  updateSaveLanguageButtonState();
}

function renderTenantSubmitButton(signedIn: boolean): void {
  const button = getButton("tenant-submit-button");
  button.textContent = signedIn ? t("change") : t("signIn");
  button.classList.toggle("button--primary", !signedIn);
  button.classList.toggle("button--secondary", signedIn);
}

function updateTenantInputSuffix(): void {
  const value = getInput("tenant-input").value.trim();
  getElement("tenant-input-suffix").hidden = value.includes(".") || value.includes("://");
}

function updateNavState(activeView: AppState["activeView"]): void {
  [
    ["create-view-button", "create"],
    ["settings-view-button", "settings"],
  ].forEach(([id, view]) => {
    const button = getButton(id);
    const isActive = activeView === view;
    button.classList.toggle("nav-button--active", isActive);
    button.setAttribute("aria-pressed", String(isActive));
  });
}

function renderRecipients(): void {
  const container = getElement("recipients");
  container.innerHTML = "";
  getElement("recipients-empty").hidden = state.recipients.length > 0;
  getElement("signing-order-help").textContent = t(
    getSelect("signing-order").value === "sequential" ? "sequentialHelp" : "parallelHelp"
  );

  const hasApprovers = state.recipients.some((recipient) => recipient.signing_method === "approval");
  const hasSigners = state.recipients.some((recipient) => recipient.signing_method !== "approval");
  const showRoleSections = hasApprovers && hasSigners;

  const groups = showRoleSections
    ? [
        {
          title: t("approvers"),
          items: state.recipients
            .map((recipient, index) => ({ recipient, index }))
            .filter(({ recipient }) => recipient.signing_method === "approval"),
        },
        {
          title: t("signers"),
          items: state.recipients
            .map((recipient, index) => ({ recipient, index }))
            .filter(({ recipient }) => recipient.signing_method !== "approval"),
        },
      ]
    : [
        {
          title: t("recipients"),
          items: state.recipients.map((recipient, index) => ({ recipient, index })),
        },
      ];

  const visibleGroups = groups.filter((group) => group.items.length);

  visibleGroups.forEach((group, groupIndex) => {
    if (showRoleSections) {
      const heading = document.createElement("h3");
      heading.className = "recipient-group-title";
      heading.textContent = group.title;
      container.appendChild(heading);
    }

    const list = document.createElement("div");
    list.className = "recipient-list";
    list.classList.toggle("recipient-list--continues", groupIndex < visibleGroups.length - 1);

    group.items.forEach(({ recipient, index }, recipientIndexWithinGroup) => {
      list.appendChild(
        createRecipientElement(recipient, index, recipientIndexWithinGroup + 1, group.items.length > 1, list)
      );
    });

    container.appendChild(list);
  });

  setControlsDisabled(state.isBusy);
}

function createRecipientElement(
  recipient: RecipientInput,
  index: number,
  orderNumber: number,
  hasMultipleItemsInList: boolean,
  list: HTMLElement
): HTMLElement {
  const row = document.createElement("div");
  const isSequential = getSelect("signing-order").value === "sequential";
  const canReorder = isSequential && hasMultipleItemsInList;
  row.className = `recipient ${canReorder ? "recipient--ordered" : "recipient--unordered"}`;
  row.setAttribute("data-recipient-index", String(index));
  row.innerHTML = `
    <button class="recipient__summary" type="button" data-action="edit">
      <span class="recipient__drag" data-action="drag" aria-hidden="true"></span>
      <span class="icon icon--user recipient__person-icon" aria-hidden="true"></span>
      <span class="recipient__identity">
        <span class="recipient__name">${escapeHtml(formatRecipientName(recipient))}</span>
        <span class="icon icon--chevron-left recipient__chevron" aria-hidden="true"></span>
        <span class="recipient__email">${escapeHtml(recipient.email || t("emailMissing"))}</span>
      </span>
      <span class="recipient__status">
        <span class="icon icon--file-import" aria-hidden="true"></span>
        <span>${escapeHtml(t("ready"))}</span>
      </span>
      ${isSequential ? `<span class="recipient__order">${orderNumber}</span>` : ""}
    </button>
  `;

  row.querySelector("[data-action='edit']")?.addEventListener("click", () => {
    if (state.suppressRecipientClick) {
      state.suppressRecipientClick = false;
      return;
    }

    if (state.draggedRecipientIndex !== undefined) {
      return;
    }

    openRecipientEditor(index);
  });

  if (canReorder) {
    const summary = row.querySelector<HTMLElement>("[data-action='edit']");

    summary?.addEventListener("pointerdown", (event) => {
      if (state.isBusy) {
        return;
      }

      const startX = event.clientX;
      const startY = event.clientY;
      let dragGhost: HTMLElement | undefined;
      let dragOffsetX = 0;
      let dragOffsetY = 0;
      let isDragging = false;
      summary.setPointerCapture(event.pointerId);

      const handlePointerMove = (moveEvent: PointerEvent) => {
        const hasMoved = Math.hypot(moveEvent.clientX - startX, moveEvent.clientY - startY) > 4;

        if (!isDragging && hasMoved) {
          isDragging = true;
          state.suppressRecipientClick = true;
          syncRecipientsFromDom();
          state.draggedRecipientIndex = index;
          row.classList.add("recipient--dragging");
          dragGhost = createRecipientDragGhost(summary);
          const rect = summary.getBoundingClientRect();
          dragOffsetX = startX - rect.left;
          dragOffsetY = startY - rect.top;
          document.body.classList.add("recipient-drag-active");
        }

        if (!isDragging) {
          return;
        }

        moveEvent.preventDefault();
        updateRecipientDragGhost(dragGhost, moveEvent.clientX - dragOffsetX, moveEvent.clientY - dragOffsetY);
        updateRecipientDropTarget(moveEvent.clientX, moveEvent.clientY, list);
      };
      const handlePointerUp = (upEvent: PointerEvent) => {
        const targetIndex = isDragging ? getRecipientIndexAtPoint(upEvent.clientX, upEvent.clientY, list) : undefined;
        dragGhost?.remove();
        document.body.classList.remove("recipient-drag-active");
        clearRecipientDropTargets();
        row.classList.remove("recipient--dragging");
        summary.releasePointerCapture(upEvent.pointerId);
        summary.removeEventListener("pointermove", handlePointerMove);
        summary.removeEventListener("pointerup", handlePointerUp);
        summary.removeEventListener("pointercancel", handlePointerUp);

        if (isDragging) {
          upEvent.preventDefault();
          moveRecipient(state.draggedRecipientIndex, targetIndex);
        }
      };

      summary.addEventListener("pointermove", handlePointerMove);
      summary.addEventListener("pointerup", handlePointerUp);
      summary.addEventListener("pointercancel", handlePointerUp);
    });
  }

  return row;
}

function createRecipientDragGhost(source: HTMLElement): HTMLElement {
  const rect = source.getBoundingClientRect();
  const ghost = source.cloneNode(true) as HTMLElement;
  ghost.classList.add("recipient-drag-ghost");
  ghost.style.width = `${rect.width}px`;
  ghost.style.height = `${rect.height}px`;
  document.body.appendChild(ghost);
  updateRecipientDragGhost(ghost, rect.left, rect.top);
  return ghost;
}

function updateRecipientDragGhost(ghost: HTMLElement | undefined, left: number, top: number): void {
  if (!ghost) {
    return;
  }

  ghost.style.transform = `translate3d(${left}px, ${top}px, 0)`;
}

function openRecipientEditor(index: number): void {
  state.editingRecipientIndex = index;
  renderRecipientEditor();
  renderSigningSubView();
  scrollToTop("auto");
  window.setTimeout(() => getInput("recipient-first-name").focus({ preventScroll: true }), 0);
}

function closeRecipientEditor(): void {
  state.editingRecipientIndex = undefined;
  renderRecipients();
  renderRecipientEditor();
  renderSigningSubView();
  updateSendButtonState();
  updateResetButtonState();
}

function renderSigningSubView(): void {
  const isEditingRecipient = state.editingRecipientIndex !== undefined;
  getElement("signing-heading").hidden = isEditingRecipient;
  getElement("signing-main").hidden = isEditingRecipient;
  getElement("recipient-editor-view").hidden = !isEditingRecipient;
  getElement("signing-footer").hidden = isEditingRecipient;
}

function renderRecipientEditor(): void {
  const index = state.editingRecipientIndex;

  if (index === undefined) {
    return;
  }

  const recipient = state.recipients[index] || createEmptyRecipient();
  getInput("recipient-first-name").value = recipient.first_name;
  getInput("recipient-last-name").value = recipient.last_name;
  getInput("recipient-email").value = recipient.email;
  getSelect("recipient-signing-method").value = recipient.signing_method;
  updateRecipientEditorHeading();
  updateAddMeButtonState();
  updateSaveRecipientButtonState();
  updateResetButtonState();
}

function saveRecipientFromEditor(): boolean {
  const index = state.editingRecipientIndex;

  if (index === undefined) {
    return false;
  }

  const recipient = readRecipientEditor();

  if (!isRecipientComplete(recipient)) {
    updateSaveRecipientButtonState();
    return false;
  }

  state.recipients[index] = recipient;
  return true;
}

function closeRecipientEditorWithoutInvalidDraft(): void {
  const index = state.editingRecipientIndex;

  if (index === undefined) {
    return;
  }

  const savedRecipient = state.recipients[index];

  if (savedRecipient && !isRecipientComplete(savedRecipient)) {
    state.recipients.splice(index, 1);
  }
}

function discardRecipientEditorChanges(): void {
  closeRecipientEditorWithoutInvalidDraft();
  closeRecipientEditor();
}

function readRecipientEditor(): RecipientInput {
  const firstName = getInput("recipient-first-name").value.trim();
  const lastName = getInput("recipient-last-name").value.trim();
  const email = getInput("recipient-email").value.trim();
  const signingMethod = getSelect("recipient-signing-method").value as SigningMethod;

  return {
    first_name: firstName,
    last_name: lastName,
    email,
    signing_method: signingMethod,
  };
}

function updateRecipientEditorHeading(): void {
  const recipient = readRecipientEditor();
  getElement("recipient-editor-title").textContent = formatRecipientName(recipient);
  getElement("recipient-editor-email").textContent = recipient.email || t("emailMissing");
}

function addCurrentUserAsRecipient(): void {
  if (!state.session?.user || !state.session.user.email || getButton("add-me-button").disabled) {
    return;
  }

  const name = splitName(state.session.user.name || state.session.user.email.replace(/@.*/, ""));
  getInput("recipient-first-name").value = name.firstName;
  getInput("recipient-last-name").value = name.lastName;
  getInput("recipient-email").value = state.session.user.email;
  getSelect("recipient-signing-method").value = "bankid";
  updateRecipientEditorHeading();
  updateAddMeButtonState();
  updateSaveRecipientButtonState();
}

function removeRecipientFromEditor(): void {
  const index = state.editingRecipientIndex;

  if (index === undefined) {
    return;
  }

  state.recipients.splice(index, 1);
  closeRecipientEditor();
}

function moveRecipient(fromIndex: number | undefined, toIndex: number | undefined): void {
  if (fromIndex === undefined || toIndex === undefined || fromIndex === toIndex) {
    state.draggedRecipientIndex = undefined;
    return;
  }

  const [recipient] = state.recipients.splice(fromIndex, 1);
  state.recipients.splice(toIndex, 0, recipient);
  state.draggedRecipientIndex = undefined;
  renderRecipients();
  updateResetButtonState();
}

function updateRecipientDropTarget(clientX: number, clientY: number, list: HTMLElement): void {
  const targetIndex = getRecipientIndexAtPoint(clientX, clientY, list);
  clearRecipientDropTargets();

  if (targetIndex === undefined || targetIndex === state.draggedRecipientIndex) {
    return;
  }

  getRecipientElement(targetIndex)?.classList.add("recipient--drop-target");
}

function clearRecipientDropTargets(): void {
  document.querySelectorAll(".recipient--drop-target").forEach((element) => {
    element.classList.remove("recipient--drop-target");
  });
}

function getRecipientIndexAtPoint(clientX: number, clientY: number, list: HTMLElement): number | undefined {
  const rows = Array.from(list.querySelectorAll<HTMLElement>("[data-recipient-index]"));
  const target = rows.find((row) => {
    const rect = row.getBoundingClientRect();
    return clientX >= rect.left && clientX <= rect.right && clientY >= rect.top && clientY <= rect.bottom;
  });
  const fallbackTarget =
    target ||
    rows.find((row) => {
      const rect = row.getBoundingClientRect();
      return clientY < rect.top + rect.height / 2;
    }) ||
    rows[rows.length - 1];
  const value = fallbackTarget?.getAttribute("data-recipient-index");

  if (value === undefined || value === null) {
    return undefined;
  }

  const index = Number(value);
  return Number.isNaN(index) ? undefined : index;
}

function getRecipientElement(index: number): HTMLElement | null {
  return document.querySelector<HTMLElement>(`[data-recipient-index="${index}"]`);
}

function syncRecipientsFromDom(): RecipientInput[] {
  if (state.editingRecipientIndex !== undefined && isRecipientComplete(readRecipientEditor())) {
    saveRecipientFromEditor();
  }

  return state.recipients;
}

function resetSigningForm(): void {
  invalidatePreparedPdf();
  getInput("signing-name").value = "";
  getInput("message").value = "";
  getSelect("language").value = getDefaultLanguage();
  getSelect("signing-order").value = "parallel";
  getSelect("excel-export-scope").value = "active-sheet";
  getSelect("excel-page-layout").value = "preserve";
  getButton("excel-export-toggle").setAttribute("aria-expanded", "false");
  getElement("excel-export-options").hidden = true;
  renderDocument();
  updateSendButtonState();
  updateResetButtonState();
}

function scrollToTop(behavior: "auto" | "smooth" = "smooth"): void {
  window.scrollTo({ top: 0, behavior });
  getElement("app-body").scrollTo({ top: 0, behavior });
}

function setBusy(isBusy: boolean, message = ""): void {
  state.isBusy = isBusy;
  const progress = getElement("progress");
  progress.hidden = !isBusy;
  progress.textContent = message;
  setControlsDisabled(isBusy);
  updateSendButtonState();
}

function setControlsDisabled(disabled: boolean): void {
  Array.from(
    document.querySelectorAll<HTMLButtonElement | HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      "button, input, textarea, select"
    )
  ).forEach((control) => {
    control.disabled =
      disabled || control.hasAttribute("data-permanent-disabled") || control.hasAttribute("data-ui-disabled");
  });
}

function updateSendButtonState(): void {
  const hasName = Boolean(getInput("signing-name").value.trim());
  const hasCompleteRecipient = state.recipients.some((recipient) => isRecipientComplete(recipient));
  const button = getButton("send-signing-button");
  const disabled =
    state.isBusy ||
    !hasName ||
    !hasCompleteRecipient ||
    !state.preparedPdf ||
    !getInput("pdf-reviewed").checked ||
    Boolean(state.pendingSigning);
  button.disabled = disabled;
  button.toggleAttribute("data-ui-disabled", disabled);

  const guidance = state.isBusy
    ? getElement("progress").textContent || t("preparingDocument")
    : state.pendingSigning
      ? t("recoveryHelp")
      : !state.preparedPdf
        ? t("pdfNotPrepared")
        : !getInput("pdf-reviewed").checked
          ? t("reviewPdfBeforeSending")
          : !hasName
            ? t("enterSigningName")
            : !hasCompleteRecipient
              ? t("addRecipientBeforeSending")
              : t("readyToSend");
  const hint = getElement("send-guidance");
  if (hint.textContent !== guidance) {
    hint.textContent = guidance;
  }
}

function updateResetButtonState(): void {
  const button = getButton("reset-signing-button");
  const disabled = state.isBusy || !hasSigningDraftChanges();
  button.disabled = disabled;
  button.toggleAttribute("data-ui-disabled", disabled);
}

function updateSaveLanguageButtonState(): void {
  const button = getButton("save-language-button");
  const disabled = state.isBusy || getSelect("settings-language").value === getDefaultLanguage();
  button.disabled = disabled;
  button.toggleAttribute("data-ui-disabled", disabled);
}

function updateSaveRecipientButtonState(): void {
  const button = getButton("save-recipient-button");
  const disabled = state.isBusy || !isRecipientComplete(readRecipientEditor());
  button.disabled = disabled;
  button.toggleAttribute("data-ui-disabled", disabled);
}

function updateAddMeButtonState(): void {
  const button = getButton("add-me-button");
  const userEmail = state.session?.user?.email?.trim().toLowerCase();
  const editor = readRecipientEditor();
  const hasStartedTyping = Boolean(
    editor.first_name || editor.last_name || editor.email || editor.signing_method !== "bankid"
  );
  const alreadyAdded = Boolean(
    userEmail &&
    state.recipients.some((recipient, index) => {
      if (index === state.editingRecipientIndex) {
        return false;
      }

      return recipient.email.trim().toLowerCase() === userEmail;
    })
  );
  const disabled = state.isBusy || !userEmail || hasStartedTyping || alreadyAdded;

  button.disabled = disabled;
  button.toggleAttribute("data-ui-disabled", disabled);
}

function isRecipientComplete(recipient: RecipientInput): boolean {
  return Boolean(
    recipient.first_name && recipient.last_name && isValidEmailAddress(recipient.email) && recipient.signing_method
  );
}

function isValidEmailAddress(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

function hasSigningDraftChanges(): boolean {
  const hasFormInput = Boolean(getInput("signing-name").value.trim() || getInput("message").value.trim());
  const hasChangedDefaults =
    getSelect("language").value !== getDefaultLanguage() ||
    getSelect("signing-order").value !== "parallel" ||
    (isExcelHost() &&
      (getSelect("excel-export-scope").value !== "active-sheet" ||
        getSelect("excel-page-layout").value !== "preserve"));
  const hasSavedRecipients = state.recipients.length > 0;
  const hasEditorInput = state.editingRecipientIndex !== undefined && hasRecipientInput(readRecipientEditor());

  return hasFormInput || hasChangedDefaults || hasSavedRecipients || hasEditorInput || Boolean(state.preparedPdf);
}

function getExcelScopeLabel(): string {
  const context = state.excelContext;
  if (getSelect("excel-export-scope").value === "workbook") {
    return context
      ? `${t("entireWorkbook")} · ${context.visibleWorksheets.length} ${t("visibleSheets")}`
      : t("entireWorkbook");
  }
  return context ? `${t("activeWorksheet")}: ${context.activeWorksheetName}` : t("activeWorksheet");
}

function renderDocument(): void {
  const excel = isExcelHost();
  const prepared = state.preparedPdf;
  const layout = getSelect("excel-page-layout").value;
  const layoutKey =
    layout === "fit-one-page" ? "fitOnePage" : layout === "fit-columns" ? "fitColumns" : "preservePageLayout";
  const scope = getSelect("excel-export-scope");
  const context = state.excelContext;
  const scopeUnsupported = excel && !supportsExcelApi("1.2");
  const layoutUnsupported = excel && !supportsExcelApi("1.9");
  const structureProtected = excel && context?.structureProtected && context.visibleWorksheets.length > 1;
  const messages: string[] = [];
  if (scopeUnsupported) {
    messages.push(t("excelScopeUnsupported"));
  }
  if (layoutUnsupported) {
    messages.push(t("excelLayoutUnsupported"));
  }
  if (structureProtected) {
    messages.push(t("excelStructureProtected"));
  }
  if (state.excelContextFailed) {
    messages.push(t("excelContextUnavailable"));
  }
  scope.querySelector<HTMLOptionElement>('option[value="active-sheet"]').disabled = Boolean(
    scopeUnsupported || structureProtected
  );
  getSelect("excel-page-layout")
    .querySelectorAll<HTMLOptionElement>("option")
    .forEach((option) => {
      option.disabled = layoutUnsupported && option.value !== "preserve";
    });
  getElement("excel-export-settings").hidden = !excel;
  getElement("excel-export-summary").textContent = `${prepared?.scope || getExcelScopeLabel()} · ${t(layoutKey)}`;
  getElement("excel-layout-help").textContent = t(
    layout === "fit-one-page" ? "fitOnePageHelp" : layout === "fit-columns" ? "fitColumnsHelp" : "preserveLayoutHelp"
  );
  getElement("excel-export-availability").textContent = messages.join(" ");
  getElement("excel-export-availability").hidden = !messages.length;
  getButton("refresh-sheets-button").hidden = !excel || !state.excelContextFailed;
  getElement("document-name").textContent = prepared?.filename || getCurrentDocumentPdfName();
  getButton("prepare-pdf-button").textContent = t(prepared ? "updatePdf" : "preparePdf");
  const unavailable = Boolean(
    (scope.value === "active-sheet" && (scopeUnsupported || structureProtected)) ||
    (layout !== "preserve" && layoutUnsupported) ||
    getPendingRestorationNames().length
  );
  getButton("prepare-pdf-button").toggleAttribute("data-ui-disabled", unavailable);
  getButton("prepare-pdf-button").disabled = state.isBusy || unavailable;
  getButton("preview-pdf-button").hidden = !prepared;
  const download = getElement("download-pdf-link") as HTMLAnchorElement;
  download.hidden = !prepared;
  if (prepared) {
    download.href = prepared.url;
    download.download = prepared.filename;
  } else {
    download.removeAttribute("href");
  }
  getElement("pdf-review").hidden = !prepared;
  getElement("pdf-status").textContent = prepared
    ? `${t("pdfReady")} · ${prepared.blob.size < 1024 * 1024 ? `${Math.max(1, Math.ceil(prepared.blob.size / 1024))} KB` : `${(prepared.blob.size / 1024 / 1024).toFixed(2)} MB`}`
    : t("pdfNotPrepared");
  const pendingNames = getPendingRestorationNames();
  getElement("excel-restoration").hidden = !pendingNames.length;
  getElement("excel-restoration-message").textContent = pendingNames.length
    ? `${t("excelRestoreFailure")} ${pendingNames.join(", ")}`
    : "";
}

function isExcelHost(): boolean {
  return Office.context.host === Office.HostType.Excel;
}

function hasRecipientInput(recipient: RecipientInput): boolean {
  return Boolean(
    recipient.first_name || recipient.last_name || recipient.email || recipient.signing_method !== "bankid"
  );
}

function hasUnsavedRecipientEditorChanges(): boolean {
  const index = state.editingRecipientIndex;

  if (index === undefined) {
    return false;
  }

  const savedRecipient = state.recipients[index] || createEmptyRecipient();
  const editedRecipient = readRecipientEditor();

  return (
    savedRecipient.first_name !== editedRecipient.first_name ||
    savedRecipient.last_name !== editedRecipient.last_name ||
    savedRecipient.email !== editedRecipient.email ||
    savedRecipient.signing_method !== editedRecipient.signing_method
  );
}

function ensureTenant(): void {
  if (!state.tenant || !isValidTenant(state.tenant)) {
    throw new Error(t("saveSubdomainFirst"));
  }
}

function showAlert(type: "success" | "error", message: string, emphasizedText = ""): void {
  const alert = getElement("alert");
  alert.hidden = false;
  alert.className = `alert alert--${type}`;
  alert.textContent = message;

  if (emphasizedText) {
    const strong = document.createElement("strong");
    strong.textContent = emphasizedText;
    alert.appendChild(strong);
  }
}

function showWorkspaceNotFoundAlert(): void {
  const alert = getElement("tenant-alert");
  const title = document.createElement("strong");
  const text = document.createElement("span");
  const example = document.createElement("span");

  alert.hidden = false;
  alert.className = "field-error";
  title.textContent = t("workspaceNotFoundTitle");
  text.textContent = t("workspaceNotFoundText");
  example.textContent = t("workspaceExample");
  alert.textContent = "";
  alert.append(title, text, example);
  getInput("tenant-input").setAttribute("aria-invalid", "true");
}

function clearTenantFieldError(): void {
  const alert = getElement("tenant-alert");
  alert.hidden = true;
  alert.className = "field-error";
  alert.textContent = "";
  getInput("tenant-input").removeAttribute("aria-invalid");
}

function clearAlert(): void {
  const alert = getElement("alert");
  alert.hidden = true;
  alert.className = "alert";
  alert.textContent = "";
}

function getElement(id: string): HTMLElement {
  const element = document.getElementById(id);

  if (!element) {
    throw new Error(`Missing element #${id}.`);
  }

  return element;
}

function getInput(id: string): HTMLInputElement {
  return getElement(id) as HTMLInputElement;
}

function getSelect(id: string): HTMLSelectElement {
  return getElement(id) as HTMLSelectElement;
}

function getButton(id: string): HTMLButtonElement {
  return getElement(id) as HTMLButtonElement;
}

function getErrorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function isWorkspaceNotFoundError(error: unknown): boolean {
  return error instanceof Error && error.name === "WorkspaceNotFoundError";
}

function openExternalUrl(url: string): void {
  const officeUi = Office.context.ui as { openBrowserWindow?: (url: string) => void };

  if (officeUi.openBrowserWindow) {
    officeUi.openBrowserWindow(url);
    return;
  }

  window.open(url, "_blank", "noopener,noreferrer");
}

function toFilename(value: string): string {
  const filename = value
    .trim()
    .replace(/[\\/:*?"<>|]+/g, "-")
    .split("")
    .filter(isSafeFilenameCharacter)
    .join("")
    .replace(/\s+/g, " ")
    .replace(/-+/g, "-")
    .replace(/^[ ._-]+|[ ._-]+$/g, "");

  return filename || "document";
}

function isSafeFilenameCharacter(character: string): boolean {
  const code = character.charCodeAt(0);
  return code >= 32 && code !== 127;
}

function getCurrentDocumentPdfName(): string {
  const url = Office.context.document?.url || "";
  const fallback = `${t("currentDocument")}.pdf`;

  if (!url) {
    return fallback;
  }

  const path = url.split(/[?#]/)[0];
  const rawName = path.split(/[\\/]/).pop() || "";
  let decodedName = rawName.trim();

  try {
    decodedName = decodeURIComponent(rawName).trim();
  } catch (_error) {
    decodedName = rawName.trim();
  }

  if (!decodedName) {
    return fallback;
  }

  const baseName = decodedName.replace(/\.[^.]+$/, "");
  return `${toFilename(baseName || t("currentDocument"))}.pdf`;
}

function formatRecipientName(recipient: RecipientInput): string {
  const name = `${recipient.first_name} ${recipient.last_name}`.trim();
  return name || t("newRecipient");
}

function renderUserAvatar(user: AuthSession["user"]): void {
  const avatar = getElement("user-avatar");
  const value = user?.name || user?.email || state.tenant;
  getElement("user-avatar-initials").textContent = getUserInitials(value);
  getElement("user-avatar-tenant").textContent = state.tenant ? `${state.tenant}.krebit.se` : t("noDomainSelected");
  getElement("user-avatar-email").textContent = user?.email ? `${t("loggedInAs")} ${user.email}` : t("notSignedIn");
  avatar.style.backgroundColor = getAvatarColor(user, value);
}

function getAvatarColor(user: AuthSession["user"], fallbackValue: string): string {
  const explicitColor = user?.avatar_color || user?.avatarColor || user?.color;

  if (explicitColor && /^#[0-9a-f]{6}$/i.test(explicitColor)) {
    return explicitColor;
  }

  const palette = ["#7133f8", "#1b5de0", "#0f766e", "#be123c", "#7c3aed", "#0369a1"];
  const hash = fallbackValue.split("").reduce((sum, char) => sum + char.charCodeAt(0), 0);
  return palette[hash % palette.length];
}

function splitName(value: string): { firstName: string; lastName: string } {
  const parts = value.trim().split(/\s+/).filter(Boolean);

  if (!parts.length) {
    return { firstName: "", lastName: "" };
  }

  if (parts.length === 1) {
    return { firstName: parts[0], lastName: "" };
  }

  return {
    firstName: parts.slice(0, -1).join(" "),
    lastName: parts[parts.length - 1],
  };
}

function getUserInitials(value: string): string {
  const parts = value
    .replace(/@.*/, "")
    .split(/[\s._-]+/)
    .filter(Boolean);

  if (!parts.length) {
    return "Kr";
  }

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function escapeHtml(value: string): string {
  return value.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}
